// ---------------------------------------------------------------------------
// ActionsPane — aba "Pendencias" do ProgressDrawerV3 (matriz executiva).
// ---------------------------------------------------------------------------

import React, { useCallback, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import CheckCircleOutline from "@material-ui/icons/CheckCircleOutline";
import InfoOutlined from "@material-ui/icons/InfoOutlined";
import { useTranslation } from "react-i18next";

import useProgressStyles from "../styles";
import useDrawerStyles from "../../drawer/styles";
import { useOverlayScrollStyles } from "../../styles";
import useNativeWheelScroll from "../../hooks/useNativeWheelScroll";
import DrawerPaneLoadingLayout from "../../DrawerPaneLoadingLayout";
import {
  PENDING_ACTION_TYPES,
  buildDetailSections,
  buildPendingMatrix,
  detailSectionId,
  filterPendingActions,
} from "./actionsPaneMatrix";

const TYPE_I18N = {
  upload: "col_upload",
  reenvio: "col_reenvio",
  decision: "col_decision",
  error: "col_error",
};

const PILL_CLASS = {
  upload: "pendPillUpload",
  reenvio: "pendPillReenvio",
  decision: "pendPillDecision",
  error: "pendPillError",
};

function PendTypePill({ classes, type, label }) {
  return (
    <span
      className={classNames(classes.pendTypePill, classes[PILL_CLASS[type]])}
    >
      {label}
    </span>
  );
}

PendTypePill.propTypes = {
  classes: PropTypes.object.isRequired,
  type: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
};

const ActionsPane = ({ items, overviewLoading }) => {
  const { t } = useTranslation();
  const classes = useProgressStyles();
  const drawerClasses = useDrawerStyles();
  const overlayScroll = useOverlayScrollStyles();
  const scrollRef = useNativeWheelScroll();
  const sectionRefs = useRef({});
  const ns = "modeling_inputs_v3.actions_pane";

  const pendingItems = useMemo(() => filterPendingActions(items), [items]);
  const matrix = useMemo(
    () => buildPendingMatrix(pendingItems),
    [pendingItems],
  );
  const detailSections = useMemo(() => buildDetailSections(matrix), [matrix]);

  const [selection, setSelection] = useState(null);

  const scrollToSection = useCallback((personKey, type) => {
    const id = detailSectionId(personKey, type);
    const el = sectionRefs.current[id];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  const handleCellSelect = useCallback(
    (personKey, type) => {
      setSelection({ personKey, type });
      scrollToSection(personKey, type);
    },
    [scrollToSection],
  );

  const colLabel = (type) => t(`${ns}.${TYPE_I18N[type]}`);
  const pillLabel = (type) => t(`${ns}.pill_${type}`);

  if (overviewLoading) {
    return (
      <DrawerPaneLoadingLayout
        variant="progress"
        title={t(`${ns}.title`)}
        message={t(`${ns}.loading`)}
      />
    );
  }

  if (!pendingItems.length) {
    return (
      <>
        <div className={classes.paneHead}>
          <h4 className={classes.paneHeadTitle}>{t(`${ns}.title`)}</h4>
        </div>
        <div className={classes.paneEmpty}>
          <CheckCircleOutline />
          <div className={classes.paneEmptyTitle}>{t(`${ns}.empty_title`)}</div>
          <div className={classes.paneEmptySub}>{t(`${ns}.empty_sub`)}</div>
        </div>
      </>
    );
  }

  const headCountLabel = t(`${ns}.head_count`, {
    count: pendingItems.length,
  });

  return (
    <>
      <div className={classes.paneHead}>
        <h4 className={classes.paneHeadTitle}>{t(`${ns}.title`)}</h4>
        <span className={classes.paneHeadCount}>{headCountLabel}</span>
      </div>
      <div
        ref={scrollRef}
        className={overlayScroll.overlayScroll}
        style={{ flex: 1, minHeight: 0 }}
      >
        <div className={classes.pendMapBody}>
          <div className={drawerClasses.xlinkExplainer}>
            <InfoOutlined className={drawerClasses.xlinkExplainerIcon} />
            <div>{t(`${ns}.header_note`)}</div>
          </div>
          <div className={classes.pendMatrixWrap}>
            <table className={classes.pendMatrix}>
              <thead>
                <tr>
                  <th
                    className={classNames(
                      classes.pendMatrixTh,
                      classes.pendMatrixThPerson,
                    )}
                  >
                    {t(`${ns}.col_person`)}
                  </th>
                  {PENDING_ACTION_TYPES.map((type) => (
                    <th key={type} className={classes.pendMatrixTh}>
                      {colLabel(type)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {matrix.map((row) => (
                  <tr key={row.personKey}>
                    <td
                      className={classNames(
                        classes.pendMatrixTd,
                        classes.pendMatrixPerson,
                      )}
                    >
                      {row.personLabel}
                    </td>
                    {PENDING_ACTION_TYPES.map((type) => {
                      const count = row.counts[type];
                      const isActive = count > 0;
                      const isSelected =
                        selection &&
                        selection.personKey === row.personKey &&
                        selection.type === type;
                      return (
                        <td
                          key={type}
                          className={classNames(
                            classes.pendMatrixTd,
                            isActive
                              ? classes.pendCellActive
                              : classes.pendCell,
                            isSelected && classes.pendCellSelected,
                          )}
                          onClick={
                            isActive
                              ? () => handleCellSelect(row.personKey, type)
                              : undefined
                          }
                          role={isActive ? "button" : undefined}
                          tabIndex={isActive ? 0 : undefined}
                          onKeyDown={
                            isActive
                              ? (e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    handleCellSelect(row.personKey, type);
                                  }
                                }
                              : undefined
                          }
                        >
                          {isActive ? count : "—"}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className={classes.pendDetailsRoot}>
          {detailSections.map((section) => (
            <div
              key={section.id}
              id={section.id}
              ref={(el) => {
                if (el) sectionRefs.current[section.id] = el;
                else delete sectionRefs.current[section.id];
              }}
              className={classes.pendDetail}
            >
              <div className={classes.pendDetailLabel}>
                {t(`${ns}.detail_label`, {
                  person: section.personLabel,
                  type: colLabel(section.type),
                  count: section.count,
                })}
              </div>
              <ul className={classes.pendDetailList}>
                {section.items.map((item) => (
                  <li key={item.id} className={classes.pendDetailItem}>
                    <PendTypePill
                      classes={classes}
                      type={item.type}
                      label={pillLabel(item.type)}
                    />
                    <span
                      className={classes.pendItemTitle}
                      dangerouslySetInnerHTML={{ __html: item.title }}
                    />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

ActionsPane.propTypes = {
  items: PropTypes.array,
  overviewLoading: PropTypes.bool,
};

ActionsPane.defaultProps = {
  overviewLoading: false,
};

export default ActionsPane;
